import { useEffect, useReducer, useRef } from "react";

const TERMINAL = new Set(["completed", "cancelled", "failed_terminal"]);
export function canSteerRun(runId: string | null, state: string | null): boolean {
  return runId !== null && state !== null && !TERMINAL.has(state);
}

export interface QueuedPrompt {
  readonly text: string;
  readonly workingDirectory?: string;
  readonly idempotencyKey: string;
}

/** In-memory FIFO: failed sends keep their original idempotency key for explicit retry. */
export class PromptQueue {
  readonly items: QueuedPrompt[] = [];
  error: string | null = null;
  sending = false;
  waitingRunId: string | null = null;

  command(instruction: string, workingDirectory = ""): string {
    const text = instruction.trim();
    if (!text || text === "status") return `${this.items.length} prompt(s) queued locally. Keep this conversation open.${this.error ? ` Failed: ${this.error}. Use /queue retry.` : ""}`;
    if (text === "clear") {
      // An in-flight request cannot be unsent. Keep its receipt until it settles.
      this.items.splice(this.sending ? 1 : 0);
      this.error = null;
      return "Pending queue cleared. An in-flight send cannot be cancelled here.";
    }
    if (text === "retry") {
      this.error = null;
      return "Retrying the local queue.";
    }
    this.items.push({ text, idempotencyKey: crypto.randomUUID(), ...(workingDirectory ? { workingDirectory } : {}) });
    return `${this.items.length} prompt(s) queued locally. Keep this conversation open.`;
  }

  async drain(
    runId: string | null,
    state: string | null,
    send: (prompt: QueuedPrompt) => Promise<string | null>,
  ): Promise<void> {
    if (this.sending || this.error || this.items.length === 0) return;
    // Wait for the newly started run, not a stale terminal snapshot of its predecessor.
    if (this.waitingRunId !== null) {
      if (runId !== this.waitingRunId || state === null || !TERMINAL.has(state)) return;
      this.waitingRunId = null;
    }
    if (runId !== null && (state === null || !TERMINAL.has(state))) return;
    const head = this.items[0]!;
    this.sending = true;
    try {
      this.waitingRunId = await send(head);
      this.items.shift();
    } catch (error) {
      this.error = error instanceof Error ? error.message : "Unable to send queued prompt";
    } finally {
      this.sending = false;
    }
  }
}

/** A queue belongs to exactly one workspace/conversation/branch and never follows navigation. */
export function usePromptQueue(input: {
  readonly scopeKey: string;
  readonly workingDirectory?: string;
  readonly runId: string | null;
  readonly runState: string | null;
  readonly ready: boolean;
  readonly send: (prompt: QueuedPrompt) => Promise<string | null>;
}) {
  const slot = useRef({ scopeKey: input.scopeKey, queue: new PromptQueue() });
  if (slot.current.scopeKey !== input.scopeKey) slot.current = { scopeKey: input.scopeKey, queue: new PromptQueue() };
  const queue = slot.current.queue;
  const [revision, changed] = useReducer((n: number) => n + 1, 0);
  const latest = useRef(input);
  latest.current = input;
  useEffect(() => {
    if (!input.ready || queue.sending || queue.error || !queue.items.length) return;
    const before = queue.items.length;
    void queue.drain(input.runId, input.runState, (prompt) => latest.current.send(prompt)).then(() => {
      if (queue.error || queue.items.length !== before) changed();
    });
  }, [queue, revision, input.ready, input.runId, input.runState]);
  return {
    count: queue.items.length,
    error: queue.error,
    // A server-selected continuation branch is not manual navigation. Carry the
    // remaining FIFO with that response, but never adopt a stale view's result.
    adoptScope: (nextKey: string) => {
      if (slot.current.queue === queue) slot.current.scopeKey = nextKey;
    },
    command: (instruction: string) => { const notice = queue.command(instruction, input.workingDirectory); changed(); return notice; },
  };
}
